import { useState } from 'react';
import { apiClient } from '../../api/client';
import type { WalkUpFormData, WalkInResult } from './types';

const panelStyle: React.CSSProperties = {
  padding: '16px',
  paddingBottom: '100px',
};

const headingStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: '16px',
  color: '#1a1a1a',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '4px',
  color: '#333',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '44px',
  padding: '10px 12px',
  fontSize: '16px',
  border: '1px solid #ccc',
  borderRadius: '6px',
  boxSizing: 'border-box',
  marginBottom: '12px',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: '#d32f2f',
  marginBottom: '4px',
};

const fieldErrorStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#d32f2f',
  marginBottom: '12px',
};

const submitButtonStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '44px',
  padding: '12px',
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#1a6b3c',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  marginTop: '8px',
};

const submitButtonDisabledStyle: React.CSSProperties = {
  ...submitButtonStyle,
  opacity: 0.6,
  cursor: 'not-allowed',
};

const successStyle: React.CSSProperties = {
  padding: '12px',
  backgroundColor: '#e8f5e9',
  border: '1px solid #4caf50',
  borderRadius: '6px',
  marginBottom: '16px',
  color: '#2e7d32',
  fontSize: '14px',
};

const warningStyle: React.CSSProperties = {
  padding: '12px',
  backgroundColor: '#fff3e0',
  border: '1px solid #ff9800',
  borderRadius: '6px',
  marginBottom: '16px',
  color: '#e65100',
  fontSize: '14px',
};

const seatListStyle: React.CSSProperties = {
  marginTop: '8px',
  paddingLeft: '16px',
};

export function WalkUpRegistrationPanel() {
  const [formData, setFormData] = useState<WalkUpFormData>({
    fullName: '',
    chapterAffiliation: '',
    dietaryPreference: '',
    accessibilityNeeds: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<WalkInResult | null>(null);
  const [noSeatsWarning, setNoSeatsWarning] = useState(false);

  function handleChange(field: keyof WalkUpFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'fullName' && fieldError) {
      setFieldError(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Client-side validation
    if (!formData.fullName.trim()) {
      setFieldError('Full name is required');
      return;
    }

    setIsSubmitting(true);
    setFieldError(null);
    setSuccessResult(null);
    setNoSeatsWarning(false);

    try {
      const payload: Record<string, string> = {
        fullName: formData.fullName.trim(),
      };
      if (formData.chapterAffiliation?.trim()) {
        payload.chapterAffiliation = formData.chapterAffiliation.trim();
      }
      if (formData.dietaryPreference?.trim()) {
        payload.dietaryPreference = formData.dietaryPreference.trim();
      }
      if (formData.accessibilityNeeds?.trim()) {
        payload.accessibilityNeeds = formData.accessibilityNeeds.trim();
      }

      const result = await apiClient<WalkInResult>(
        '/table-management/guests/walk-in',
        { method: 'POST', body: payload },
      );

      if (result.noSeatsAvailable) {
        setNoSeatsWarning(true);
        setSuccessResult(result);
      } else {
        setSuccessResult(result);
      }

      // Clear form on success
      setFormData({
        fullName: '',
        chapterAffiliation: '',
        dietaryPreference: '',
        accessibilityNeeds: '',
      });
    } catch (err: unknown) {
      const error = err as { statusCode?: number; message?: string | string[] };
      if (error.statusCode === 400) {
        const msg = Array.isArray(error.message) ? error.message[0] : error.message;
        setFieldError(msg || 'Full name is required');
      } else {
        setFieldError('An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div style={panelStyle}>
      <h2 style={headingStyle}>Walk-Up Registration</h2>

      {successResult && !noSeatsWarning && (
        <div style={successStyle} role="status">
          <strong>Registered:</strong> {successResult.guest.fullName}
          {successResult.suggestedSeats.length > 0 && (
            <>
              <div style={{ marginTop: '6px' }}>Suggested seat assignments:</div>
              <ul style={seatListStyle}>
                {successResult.suggestedSeats.map((seat) => (
                  <li key={seat.seatId}>
                    Table {seat.tableNumber} ({seat.tableName}), Seat {seat.seatNumber} — {seat.zoneName}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {noSeatsWarning && (
        <div style={warningStyle} role="alert">
          No seats currently available. {successResult?.guest.fullName} has been registered but could not be assigned a seat.
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div>
          <label style={labelStyle} htmlFor="walkup-fullName">
            Full Name <span aria-hidden="true">*</span>
          </label>
          <input
            id="walkup-fullName"
            type="text"
            style={fieldError ? inputErrorStyle : inputStyle}
            value={formData.fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
            required
            aria-required="true"
            aria-invalid={!!fieldError}
            aria-describedby={fieldError ? 'walkup-fullName-error' : undefined}
            placeholder="Enter guest full name"
          />
          {fieldError && (
            <div id="walkup-fullName-error" style={fieldErrorStyle} role="alert">
              {fieldError}
            </div>
          )}
        </div>

        <div>
          <label style={labelStyle} htmlFor="walkup-chapter">
            Chapter Affiliation
          </label>
          <input
            id="walkup-chapter"
            type="text"
            style={inputStyle}
            value={formData.chapterAffiliation}
            onChange={(e) => handleChange('chapterAffiliation', e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="walkup-dietary">
            Dietary Preference
          </label>
          <input
            id="walkup-dietary"
            type="text"
            style={inputStyle}
            value={formData.dietaryPreference}
            onChange={(e) => handleChange('dietaryPreference', e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="walkup-accessibility">
            Accessibility Needs
          </label>
          <input
            id="walkup-accessibility"
            type="text"
            style={inputStyle}
            value={formData.accessibilityNeeds}
            onChange={(e) => handleChange('accessibilityNeeds', e.target.value)}
            placeholder="Optional"
          />
        </div>

        <button
          type="submit"
          style={isSubmitting ? submitButtonDisabledStyle : submitButtonStyle}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Registering…' : 'Register Walk-Up Guest'}
        </button>
      </form>
    </div>
  );
}
